/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {SSOConfiguration} from '@ion/core/src/types/entities';
import CTALink from '@ion/dls/src/components/buttons/CTALink';
import DeletePrompt from '@ion/dls/src/editors/DeletePrompt';
import useShowDeletePrompt from '@ion/dls/src/hooks/useShowDeletePrompt';
import Render from '@ion/dls/src/render/Render';
import {
  dlsColor,
  dlsTypography
} from '@ion/dls/src/theme/theme';
import DeleteResultPrompt from '@ion/features/src/prompts/DeleteResultPrompt';
import {useDeleteSSOConfiguration} from '@ion/query/src/hooks/useDeleteSSOConfiguration';

export interface DeleteSSOConfigurationProps {
  ssoConfig: SSOConfiguration;
  setIsSSOConfigDeleted?: Function;
  isSSOConfigDeleted?: boolean;
}

const useStyles = makeStyles(theme => ({
  ctaLink : {
    ...dlsTypography('cta'),
    color         : dlsColor('caRed'),
    cursor        : 'pointer',
    letterSpacing : '1px',
    lineHeight    : 'inherit',
    fontSize      : 'inherit',
    display       : 'block',
    alignItems    : 'center',
    '&&:hover'    : {
      textDecoration : 'underline'
    }
  }
}));

/**
 * The DeleteSSOConfiguration component provides the UI which allows to 
 * delete the SSO Configuration.
 *
 * ```typescript
 *
 *    import {
 *      DeleteSSOConfiguration
 *    } from '@ion/applications/src/pages/admin/accounts/DeleteSSOConfiguration';
 *
 *    <DeleteSSOConfiguration ssoConfig={ssoConfiguration}
 *     setIsSSOConfigDeleted={setIsSSOConfigDeleted}
 *     isSSOConfigDeleted={isSSOConfigDeleted} />
 *
 * ```
 */
export const DeleteSSOConfiguration = ({
  ssoConfig,
  setIsSSOConfigDeleted,
  isSSOConfigDeleted = false
}: DeleteSSOConfigurationProps) => {
  const classes           = useStyles();
  const [show, setShow]   = useState(false);
  const {id: ssoConfigId} = ssoConfig || {};
  const entityName        = translate('common:entityLabels.ssoConfiguration');
  const overrideMessage   = useCallback(() => (
    translate('admin-groups:dialog.ssoConfiguration.deleteWarning')
  ), []);

  const {
    deleteSSOConfig,
    isLoading,
    isError,
    isSuccess
  } = useDeleteSSOConfiguration();

  const {
    setShowError,
    setShowSuccess,
    showSuccess,
    showError,
    closeSnack
  } = useShowDeletePrompt();

  useEffect(() => {
    if (isError && !isLoading) {
      onDeleteCancel();
      setShowError(isError);
    }
  }, [isError, isLoading]);

  useEffect(() => {
    if (isSuccess && !isLoading) {
      setIsSSOConfigDeleted(true);
      setShowSuccess(true);
      setShow(false);
    }
  }, [isSuccess, isLoading]);

  const handleDelete = useCallback(() => {
    setShow(true);
  }, []);

  const onDeleteConfirm = useCallback(() => {
    deleteSSOConfig(ssoConfigId);
  }, [ssoConfigId]);

  const onDeleteCancel = useCallback(() => {
    setShow(false);
  }, []);

  return (
    <>
      <Render.If condition={!isSSOConfigDeleted}>
        <CTALink className={classes.ctaLink}
          onClick={handleDelete}>
          {translate('admin-groups:dialog.ssoConfiguration.delete')}
        </ CTALink>
      </Render.If>
      <DeletePrompt show={show}
        entity={entityName}
        items={ssoConfig}
        isLoading={isLoading}
        overrideMessage={overrideMessage}
        onConfirm={function() {
          onDeleteConfirm();
        }}
        onCancel={function() {
          onDeleteCancel();
        }} />
      <DeleteResultPrompt entityTitle={entityName}
        isError={showError}
        isSuccess={showSuccess}
        onAction={closeSnack} />
    </>
  );
};

export default DeleteSSOConfiguration;
